<template>
	<div class="image" @mouseleave="triggerHideCursor" @mouseenter="triggerShowCursor">
		<div class="cursor" :class="{'-visible': showCursor}" :style="cursorPosition" ref="cursor">
			<div class="position">
				<span class="emphasize">
					<div class="slide">
						<span class="left" :style="{transform: 'translateY(' + -firstDigit * 100 + '%)'}">
							<span>0</span>
							<span>1</span>
							<span>2</span>
							<span>3</span>
							<span>4</span>
							<span>5</span>
							<span>6</span>
							<span>7</span>
							<span>8</span>
							<span>9</span>
						</span>
					</div>
					<div class="slide">
						<span class="right" :style="{transform: 'translateY(' + -(secondDigit+1) * 100 + '%)'}">
							<span>0</span>
							<span>1</span>
							<span>2</span>
							<span>3</span>
							<span>4</span>
							<span>5</span>
							<span>6</span>
							<span>7</span>
							<span>8</span>
							<span>9</span>
						</span>
					</div>
				</span>
				<span class="total">/ {{images.length}}</span>
			</div>
			<div class="helper text-center" ref="helper">
				<span>click and drag</span>
				<span>to navigate</span>
			</div>
		</div>

		<div class="absolute" ref="backContainer">
			<span class="layer" ref="layer"></span>
			<transition-group name="list" tag="ul" :class="{'inline': loaded}" :style="{transform: 'translateX(' + deltaFront + '%)'}" ref="back">
				<li v-for="(image, i) in images" :style="{ backgroundImage: 'url(' + image.url + ')', zIndex: images.length - i}" :key="i"></li>
			</transition-group>
		</div>

		<component v-bind:is="mask" class="svg" v-if="!loadingStarted"></component>

		<div class="mask" :style="{maskImage: 'url(' + maskUrl + ')'}" ref="frontContainer" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp">
			<ul class="inline" :style="{transform: 'translateX(' + deltaBack + '%)'}" ref="front">
				<li v-for="(image, i) in images" :style="{ backgroundImage: 'url(' + image.url + ')', zIndex: images.length - i}" :key="i"></li>
			</ul>
		</div>

		<div class="label text-center" ref="label">
			<span>You are </span>
			<span>flying to</span>
			<b>{{category}}</b>
		</div>

		<masking-border :visible="mouseDown" :delay="maskBorderDelayOut"></masking-border>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import LoaderMixin from '@/mixins/LoaderMixin'

// icons
import Australia from '@/assets/categories/australia.svg'
import NewZealand from '@/assets/categories/new-zealand.svg'
import CookIslands from '@/assets/categories/cook-islands.svg'
import NewCaledonia from '@/assets/categories/new-caledonia.svg'
import FrenchPolynesia from '@/assets/categories/french-polynesia.svg'
import France from '@/assets/categories/france.svg'
import Norway from '@/assets/categories/norway.svg'
import Iceland from '@/assets/categories/iceland.svg'
import Indonesia from '@/assets/categories/indonesia.svg'

// animation
import anime from 'animejs'

// components
import MaskingBorder from '@/components/MaskingBorder'

export default {
	name: 'Viewer',
	mixins: [LoaderMixin],
	components: {
		'masking-border': MaskingBorder,
		'australia': Australia,
		'new-zealand': NewZealand,
		'cook-islands': CookIslands,
		'new-caledonia': NewCaledonia,
		'french-polynesia': FrenchPolynesia,
		'france': France,
		'norway': Norway,
		'iceland': Iceland,
		'indonesia': Indonesia
	},
	data () {
		return {
			showCursor: false,
			manifest: [],
			images: [],
			index: 0,
			previousIndex: 0,
			state: false,
			loaded: false,
			loadingStarted: false,
			mouseDown: false,
			maskBorderDelayOut: 1600,
			isMoving: false,
			originalX: 0,
			pageX: 0,
			pageY: 0,
			x: window.innerWidth / 2,
			y: window.innerHeight / 2,
			deltaFront: 0,
			deltaBack: 0,
			originalDelta: 0,
			linearDelta: 0,
			cursorWidth: 0,
			cursorHeight: 0,
			helperVisible: true,
			maskUrl: null,
			firstPositionReserved: false
		}
	},
	computed: {
		...mapGetters({
			appWidth: 'width',
			appHeight: 'height',
			filtered: 'filtered',
			mask: 'mask',
			category: 'category',
			color: 'color',
			device: 'device'
		}),
		cursorPosition () {
			return {
				transform: `translateX(${this.pageX - this.cursorWidth}px) translateY(${this.pageY - this.cursorHeight}px)`,
				color: this.color
			}
		},
		firstDigit () {
			return Math.floor((this.index / 10) % 10)
		},
		secondDigit () {
			return Math.floor((this.index / 1) % 10)
		}
	},
	methods: {
		...mapActions([
			'filterImages'
		]),
		handleMouseDown (e) {
			if (!this.loaded) return
			this.mouseDown = true
			this.originalX = e.pageX
			this.scaleDown()
			if (this.helperVisible) this.hideHelper()
		},
		scaleDown () {
			if (this.scaleUpFunction) this.scaleUpFunction.pause()

			let ratio = (this.appWidth - (4 * 60)) / this.appWidth

			this.scaleDownFunction = anime({
				targets: [this.$refs.backContainer, this.$refs.frontContainer],
				scale: ratio,
				easing: 'easeOutQuint'
			})
		},
		scaleUp () {
			if (this.scaleDownFunction) this.scaleDownFunction.pause()

			this.scaleUpFunction = anime({
				targets: [this.$refs.frontContainer, this.$refs.backContainer],
				scale: 1,
				easing: 'easeOutQuint'
			})
		},
		handleMouseUp (e) {
			this.originalDelta = this.linearDelta

			let direction = e.pageX - this.originalX
			let distance = Math.abs(direction)

			if (distance > this.appWidth / 5) {
				if (Math.sign(direction) === -1) {
					this.next()
				} else if (Math.sign(direction) === 1) {
					this.prev()
				}
			} else {
				this.moveToIndex(this.index)
			}

			this.maskBorderDelayOut = (this.previousIndex === this.index) ? 0 : 1050
			this.mouseDown = false
			// store previous index
			this.previousIndex = this.index
		},
		next () {
			if (this.index + 1 < this.images.length) this.moveToIndex(this.index + 1)
			else this.moveToIndex(this.images.length - 1)
		},
		prev () {
			if (this.index - 1 < 0) this.moveToIndex(0)
			else this.moveToIndex(this.index - 1)
		},
		handleMouseMove (e) {
			this.x = e.pageX
			this.y = e.pageY
			if (this.mouseDown) this.linearDelta = this.originalDelta + (((this.x - this.originalX) * 100) / this.appWidth)
		},
		loadingLabelIn () {
			anime({
				targets: [this.$refs.label.children],
				translateY: [40, 0],
				opacity: [0, 1],
				easing: 'easeOutExpo',
				duration: 2000,
				delay: function (el, i) { return 350 + i * 75 }
			}).complete = () => {
				this.load(this.manifest)
			}
		},
		loadingLabelOut () {
			anime.timeline()
				.add({
					targets: [this.$refs.label.children],
					translateY: 40,
					opacity: 0,
					easing: 'easeOutExpo',
					offset: 200
				})
				.add({
					targets: this.$refs.layer,
					scaleX: 0,
					easing: 'easeOutExpo',
					duration: 0
				})
				.complete = () => {
					this.loaded = true
					this.showCursor = true
					this.$store.dispatch('updateColor', this.filtered[this.index].data.contrastcolour || 'white')
				}
		},
		updatedCursorPosition () {
			this.pageX += (this.x - this.pageX) * 0.1
			this.pageY += (this.y - this.pageY) * 0.1
			requestAnimationFrame(this.updatedCursorPosition)
		},
		updatedDelta () {
			if (!this.isMoving) {
				this.deltaFront += (this.linearDelta - this.deltaFront) * 0.3
				this.deltaBack += (this.linearDelta - this.deltaBack) * 0.1
			}
			requestAnimationFrame(this.updatedDelta)
		},
		onFileLoad (file) {
			// let image = {url: file.file.src, id: file.id.slug, color: file.id.color}
			if (!this.firstPositionReserved) {
				this.images.unshift({url: file.file.src, id: file.id.slug, color: file.id.color})
				if (file.id.slug === this.$route.params.id) this.firstPositionReserved = true
			} else {
				this.images.push({url: file.file.src, id: file.id.slug, color: file.id.color})
			}

			if (!this.loadingStarted) this.loadingStarted = true
		},
		onLoadComplete () {
			this.loadingLabelOut()
		},
		onSectionLeave (el, done) {
			this.$bus.$emit('transitioner:show', done)
		},
		moveToIndex (index, delay) {
			let doScale = true
			// check against previous index
			if (this.index === index) {
				this.scaleUp()
				doScale = false
			}
			// set local index to new value
			this.index = index
			// set the flag to stop raf lerp update
			this.isMoving = true
			// compute position to move to
			let position = -index * 100
			// animate front and back elements
			anime({
				targets: [this.$refs.back.$el, this.$refs.front],
				translateX: `${position}%`,
				easing: 'easeOutQuint',
				delay: delay || 0,
				duration: function (el, i, l) {
					return 1000 + (i * 250)
				}
			}).complete = () => {
				this.originalDelta = position
				this.linearDelta = position
				this.deltaFront = position
				this.deltaBack = position
				this.isMoving = false
				this.$store.dispatch('updateColor', this.images[this.index].color || 'white')
				this.updateUrl()
				if (doScale) this.scaleUp()
			}
		},
		updateUrl () {
			this.$router.push({
				name: 'viewer',
				params: {
					id: this.images[this.index].id
				}
			})
		},
		hideHelper () {
			this.helperVisible = false
			anime({
				targets: [this.$refs.helper.children],
				translateY: 20,
				opacity: 0,
				easing: 'easeOutQuint',
				duration: function (el, i, l) {
					return 1000 + (i * 250)
				}
			})
		},
		triggerHideCursor () {
			this.showCursor = false
		},
		triggerShowCursor () {
			this.showCursor = this.loaded || false
		},
		keyboardShortcuts (event) {
			switch (event.keyCode) {
			case 27 :
				this.$router.push({ name: 'list' })
				break
			case 39 :
				this.next()
				break
			case 37 :
				this.prev()
				break
			}
		},
		bindKeyboardEvents () {
			window.addEventListener('keyup', this.keyboardShortcuts)
		}

	},
	created () {
		// object containing images data
		this.filterImages(this.$route.params.category)
		// fetch images that match the passed country
		this.manifest = this.filtered.map(i => {
			return {url: (this.device !== 'desktop') ? ((i.data.image['tablet']) ? i.data.image['tablet'].url : i.data.image.url) : i.data.image.url, id: {slug: i.id, color: i.data.contrastcolour}}
		})
		// update cursor
		this.updatedCursorPosition()
		// update image delta
		this.updatedDelta()
	},
	mounted () {
		// set mask url
		this.maskUrl = `/static/categories/${this.mask}.svg`
		// store cursor size
		this.cursorWidth = this.$refs.cursor.clientWidth / 2
		this.cursorHeight = this.$refs.cursor.clientHeight / 2
		// start the thing when app is ready
		this.$bus.$once('section:ready', this.loadingLabelIn.bind(this))
		// bind key event
		this.bindKeyboardEvents()
	},
	destroyed () {
		window.removeEventListener('keyup', this.keyboardShortcuts)
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
	ul {
		list-style-type: none;
		padding: 0;
		width: 100%;
		margin: 0;
		height: 100%;
		display: flex;
		flex-wrap: nowrap;
	}
	li {
		margin: 0 30px;
		flex: 0 0 calc(100% - 60px);
		position: absolute;
		width: calc(100% - 60px);
		height: 100%;
		background-size: cover;
		background-repeat: no-repeat;
		background-position: center;
		transition: background 0.5s ease;
	}
	@media only screen and (max-width: 500px) {
		li{
			background-size: contain;
		}
	}
	ul.inline li {
		position: relative;
	}
	.image{
		width: 100%;
		height: 100%;
		position: absolute;
		margin: 0 auto;
		top: 0;
		left: 0;
		overflow: hidden;
		cursor: grab;
	}
	.image:active{
		cursor: grabbing;
	}
	.mask{
		width: 100%;
		height: 100%;
		mask-size: 20%;
		mask-repeat: no-repeat;
		mask-position: center;
		z-index: 3;
	}
	@media only screen and (max-width: 500px) {
		.mask{
			mask-size: 60%;
		}
	}
	.full {
		opacity: 0;
		transition: opacity 1s ease;
	}
	.absolute{
		position: absolute;
		width: 100%;
		height: 100%;
		z-index: 0;
	}
	.layer{
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: white;
		position: absolute;
		transform: scaleX(1);
		transition: all 0.8s ease;
		transition-delay: 1s;
		transform-origin: right;
		z-index: 10;
	}

	.list-enter-active, .list-leave-active {
		transition: all 0.4s;
		transition-delay: 0.2s;
	}
	.list-enter, .list-leave-to {
		opacity: 0;
		will-change: opacity;
	}
	.cursor{
		font-family: 'Merriweather';
		position: absolute;
		width: auto;
		height: auto;
		z-index: 10;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.5s ease, color 0.5s ease;
		text-transform: uppercase;
		color: white;
	}
	.cursor .helper span{
		display: block;
	}
	.cursor.-visible{
		opacity: 1;
	}
	.position {
		display: flex;
		justify-content: center;
	}
	.position .emphasize {
		font-size: 5rem;
		display: flex;
		width: 6rem;
	}
	.position .emphasize div{
		display: flex;
	}
	.position .total {
		line-height: 5rem;
		align-self: flex-start;
		margin-left: 0.5rem;
	}
	.helper {
		font-size: 0.55rem;
	}
	.helper span{
		display: block;
	}
	.label{
		position: absolute;
		bottom: 20%;
		width: 100%;
	}
	.label span{
		opacity: 0;
	}
	.label b{
		opacity: 0;
	}
	.slide {
		height: 6.5rem;
		overflow: hidden;
		width: 50%;
	}
	.left, .right{
		display: flex;
		flex-direction: column;
	}

	.left{
		transition: transform 0.4s cubic-bezier(1, 0, 0, 1);
		transition-delay: 0.3s;
	}

	.right{
		transition: transform 0.4s cubic-bezier(1, 0, 0, 1);
		transition-delay: 0.4s;
	}

	.left span, .right span{
		min-height: 100%;
		line-height: 7rem;
	}

	.label span, b{
		display: inline-block;
	}
</style>



// WEBPACK FOOTER //
// src/sections/Viewer.vue